import apiClient from './api'

/**
 * @typedef {Object} ProductImage
 * @property {string} imageUrl
 * @property {number} displayOrder
 * @property {boolean} isPrimary
 */

/**
 * @typedef {Object} ProductVariant
 * @property {number} variantId
 * @property {number} colorId
 * @property {string} colorName
 * @property {string} colorHexCode
 * @property {string} sku
 * @property {string} size
 * @property {number} variantListPrice
 * @property {number} variantFinalPrice
 * @property {number} stockQuantity
 * @property {string} stockLevel - 'high' | 'medium' | 'low' | 'out'
 */

/**
 * @typedef {Object} Product
 * @property {number} productId
 * @property {number} categoryTypeId
 * @property {number} listPrice
 * @property {number} vatPercentage
 * @property {number} discountPercentage
 * @property {number} finalPrice
 * @property {boolean} isActive
 * @property {boolean} isNew
 * @property {boolean} isLimitedEdition
 * @property {string} createdAt
 * @property {ProductVariant[]} variants
 * @property {ProductImage[]} images
 */

/**
 * @typedef {Object} CatalogFiltersResponse
 * @property {Array<{colorId: number, colorName: string, colorHexCode: string}>} availableColors
 * @property {string[]} availableSizes
 * @property {number} minPrice
 * @property {number} maxPrice
 */

/**
 * @typedef {Object} CatalogProductsRequest
 * @property {number} [categoryTypeId]
 * @property {boolean} [isNew]
 * @property {boolean} [isLimitedEdition]
 * @property {string} [colors] - "1,2,3"
 * @property {string} [sizes] - "M,L,XL"
 * @property {number} [minPrice]
 * @property {number} [maxPrice]
 * @property {string} [searchTerm]
 * @property {string} [sortBy] - 'latest' | 'oldest' | 'price_asc' | 'price_desc'
 * @property {number} [skip]
 * @property {number} [take]
 */

class CatalogService {
  constructor() {
    this.baseUrl = '/Catalog'
    this.maxRetries = 2
  }

  /**
   * Perform API call with retry logic
   * @private
   * @param {Function} apiCall - Async function that makes the API call
   * @returns {Promise<any>}
   */
  async _callWithRetry(apiCall) {
    let lastError
    
    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      try {
        const response = await apiCall()
        return response.data
      } catch (error) {
        lastError = error
        console.error(`[CatalogService] Attempt ${attempt + 1} failed:`, error.message)
        
        if (attempt < this.maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)))
        }
      }
    }
    
    console.error('[CatalogService] API call failed after retries:', lastError?.message)
    throw lastError || new Error('Failed to fetch data from Catalog API')
  }

  /**
   * Get paginated catalog with filters
   * @param {CatalogProductsRequest} params
   * @returns {Promise<Product[]>}
   */
  async getProducts(params = {}) {
    const queryParams = new URLSearchParams()
    
    if (params.categoryId !== undefined) queryParams.append('categoryId', params.categoryId)
    if (params.categoryTypeId !== undefined) queryParams.append('categoryTypeId', params.categoryTypeId)
    if (params.isNew !== undefined) queryParams.append('isNew', params.isNew)
    if (params.isLimitedEdition !== undefined) queryParams.append('isLimitedEdition', params.isLimitedEdition)
    if (params.colors) queryParams.append('colors', params.colors)
    if (params.sizes) queryParams.append('sizes', params.sizes)
    if (params.minPrice !== undefined) queryParams.append('minPrice', params.minPrice)
    if (params.maxPrice !== undefined) queryParams.append('maxPrice', params.maxPrice)
    if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm)
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.skip !== undefined) queryParams.append('skip', params.skip)
    if (params.take !== undefined) queryParams.append('take', params.take)

    const url = `${this.baseUrl}/products${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    
    return this._callWithRetry(() => apiClient.get(url))
  }

  /**
   * Get single product by ID
   * @param {number} id - Product ID
   * @param {number} [variantId] - Optional variant ID
   * @returns {Promise<Product>}
   */
  async getProductById(id, variantId) {
    if (!id) {
      throw new Error('Product ID is required')
    }

    let url = `${this.baseUrl}/products/${id}`
    if (variantId !== undefined) {
      url += `?variantId=${variantId}`
    }

    return this._callWithRetry(() => apiClient.get(url), 'products')
  }

  /**
   * Get filter options (colors, sizes, prices)
   * @param {Object} params - Query parameters
   * @param {number} [params.categoryId] - Category ID
   * @param {number} [params.categoryTypeId] - Category Type ID
   * @returns {Promise<CatalogFiltersResponse>}
   */
  async getFilters(params = {}) {
    const queryParams = new URLSearchParams()
    
    if (params.categoryId !== undefined) queryParams.append('categoryId', params.categoryId)
    if (params.categoryTypeId !== undefined) queryParams.append('categoryTypeId', params.categoryTypeId)

    const url = `${this.baseUrl}/filters${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    
    return this._callWithRetry(() => apiClient.get(url))
  }

  /**
   * Get all categories with localized data
   * @param {string} [lang] - Language code ('it' or 'en')
   * @returns {Promise<Object>}
   */
  async getCategories(lang = 'it') {
    const url = `/categories?lang=${lang}`
    return this._callWithRetry(() => apiClient.get(url))
  }
}

export const catalogService = new CatalogService()
